// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { formatTime } from '@seedplanner/shared-utils';
import type dayjs from 'dayjs';
import type Decimal from 'decimal.js';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Typography } from '../../lib/base';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';

export interface AddOrderChangeInfo {
  readonly cropId: string;
  readonly cropName: string;
  readonly traysNeeded: Decimal;
  readonly surplusTraysAvailable: Decimal;
  readonly plantingDateInfo?: {
    readonly startDate: dayjs.Dayjs;
    readonly canBeStartedInTime: boolean;
    readonly harvestDate: dayjs.Dayjs;
  };
}

interface Props {
  readonly plantings: AddOrderChangeInfo[];
  readonly useSurpluses: boolean;
  readonly createOverduePlantings: boolean;
}

export default function AddOrderChangesTable({ plantings, useSurpluses, createOverduePlantings }: Props) {
  const { timezone } = useUserContext();
  const columns: ColumnType[] = [
    {
      name: 'Crop',
      values: plantings.map((change) => change.cropName),
    },
    {
      name: 'Start',
      values: plantings.map((change) =>
        change.plantingDateInfo ? (
          <Typography
            key={change.cropName}
            sx={{
              // Taken from classes.row in Column.tsx
              fontFamily: 'fontFamily',
              fontSize: 14,
              fontWeight: 'fontWeightRegular',
              color: change.plantingDateInfo.canBeStartedInTime ? 'text.primary' : 'error.main',
              // textRowBase in getTableStyles.ts
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {formatTime(change.plantingDateInfo.startDate.unix(), {
              timezone,
            })}
          </Typography>
        ) : (
          '-'
        ),
      ),
      visibleAt: 'sm',
    },
    {
      name: 'Harvest',
      values: plantings.map((change) =>
        change.plantingDateInfo
          ? formatTime(change.plantingDateInfo.harvestDate.unix(), {
              timezone,
            })
          : '-',
      ),
      visibleAt: 'md',
    },
    {
      name: 'Trays',
      values: plantings.map((change) => change.traysNeeded.toFixed(2)),
      numeric: true,
      visibleAt: 'xs',
    },
    {
      name: 'Surplus',
      values: plantings.map((change) => change.surplusTraysAvailable.toFixed(2)),
      numeric: true,
      visibleAt: 'sm',
    },
    {
      name: 'Action',
      // If too late to plant and useSurpluses is false, then create new planting
      // If too late to plant and useSurpluses is true, then show how many trays will be pulled from surplus
      // If not too late and useSurpluses is true, then show how many trays will be pulled from surplus
      // If not too late and useSurpluses is false, then create new planting
      values: plantings.map((change) => {
        if (change.plantingDateInfo === undefined) {
          return '-';
        }
        if (useSurpluses) {
          const hasEnoughSurplus = change.surplusTraysAvailable.gte(change.traysNeeded);
          const hasAnySurplus = change.surplusTraysAvailable.gt(0);
          const traysStillNeededAfterSurplus = change.traysNeeded.sub(change.surplusTraysAvailable);

          if (hasEnoughSurplus) {
            return `Pull ${change.traysNeeded.toFixed(2)} Trays From Surplus`;
          }
          if (hasAnySurplus) {
            const traysStillNeeded = traysStillNeededAfterSurplus.toFixed(2);
            if (createOverduePlantings || change.plantingDateInfo.canBeStartedInTime) {
              return `${change.surplusTraysAvailable.toFixed(2)} Trays From Surplus. Create ${traysStillNeeded} Tray Planting`;
            }

            // Pull from surplus and don't create the overdue trays needed
            return `Pull ${change.surplusTraysAvailable.toFixed(2)} Trays From Surplus`;
          }
          if (change.plantingDateInfo.canBeStartedInTime) {
            return `Create ${change.traysNeeded.toFixed(2)} Tray Planting`;
          }

          return createOverduePlantings
            ? `Create ${change.traysNeeded.toFixed(2)} Tray Planting`
            : 'Nothing. Too Late To Start';
        }

        const traysNeededMsg = `Create ${change.traysNeeded.toFixed(2)} Tray Planting`;
        if (createOverduePlantings || change.plantingDateInfo.canBeStartedInTime) {
          return traysNeededMsg;
        }

        return 'Nothing. Too Late To Start';
      }),
    },
  ];

  return <Table columns={columns} />;
}
